// ── Press Feedback (reactive press-down / pop-up) ────────────────────────
// Stateful press primitive. The render loop drives one active press per
// frame; visuals follow the actual key/button state instead of replaying a
// fixed animation triggered on release.
//
//   beginPress(target)  — visual scale-down begins (key down)
//   endPress()          — rebound with pop begins, returns press duration
//   cancelPress()       — abort silently (no rebound)
//   stepPressFeedback(now) — call once per rAF frame
//
// Hold longer than APP_PRESS_DOWN_MS and the icon stays at the depressed
// scale — that is the visual cue that the press is being held. Release
// at any time and it pops back to 1 with overshoot.
//
// Two target adapters:
//   • ItemPressTarget — writes to Item.pressScaleMul; the existing render
//     pipeline (mainLoop, tubeLayoutEngine) multiplies it into the item's
//     final scale.
//   • ElementPressTarget — writes a style.transform: scale(...) directly to
//     a DOM element via the commit helper. Use for selectables that are not
//     ring items (e.g. options-menu rows).

import type { Item } from '../types';
import { state } from '../state';
import { lerp, clamp, easeOutCubic, easeOutBack } from '../utils/math';
import { APP_PRESS_DOWN_SCALE, APP_PRESS_DOWN_MS, APP_PRESS_REBOUND_MS } from '../config';
import { isReducedMotion, getReducedMotionDuration } from '../utils/reducedMotion';
import { setStyleIfChanged } from '../layout/commit';
import { applyItemTransform } from '../layout/ringLayout';

export interface PressTarget {
  applyScale(scale: number): void;
  reset(): void;
}

class ItemPressTarget implements PressTarget {
  constructor(private readonly item: Item) {}
  applyScale(scale: number): void {
    this.item.pressScaleMul = scale;
    this.item.pressed = scale < 0.999;
    // Write transform directly so the press visual updates even when the
    // home interactive phase is gated (during launch animation, for
    // example). getItemRenderScale picks up pressScaleMul.
    applyItemTransform(this.item);
  }
  reset(): void {
    this.item.pressScaleMul = 1;
    this.item.pressed = false;
    applyItemTransform(this.item);
  }
}

class ElementPressTarget implements PressTarget {
  constructor(private readonly el: HTMLElement) {}
  applyScale(scale: number): void {
    setStyleIfChanged(this.el, 'transform', `scale(${scale.toFixed(3)})`);
  }
  reset(): void {
    // Clear the inline override so any CSS-driven transform (entry
    // animations, hover states) takes over again.
    setStyleIfChanged(this.el, 'transform', '');
  }
}

class ClockPressTarget implements PressTarget {
  applyScale(scale: number): void {
    // Don't write the DOM directly — the lockScreen transform is rebuilt
    // every frame in mainLoop's home branch from camera + persistentClockScale.
    // Writing here would just be overwritten next tick. Instead drop the
    // press multiplier into state and let mainLoop fold it in.
    state.clockPressScale = scale;
  }
  reset(): void {
    state.clockPressScale = 1;
  }
}

export function pressItem(item: Item): PressTarget {
  return new ItemPressTarget(item);
}

export function pressElement(el: HTMLElement): PressTarget {
  return new ElementPressTarget(el);
}

export function pressClock(): PressTarget {
  return new ClockPressTarget();
}

interface ActivePress {
  target: PressTarget;
  startedAt: number;
  releasedAt: number | null;
  releasedScale: number;
  downDuration: number;
  reboundDuration: number;
}

let active: ActivePress | null = null;

function computeDownScale(elapsed: number, downDuration: number): number {
  const t = clamp(elapsed / Math.max(1, downDuration), 0, 1);
  return lerp(1, APP_PRESS_DOWN_SCALE, easeOutCubic(t));
}

export function beginPress(target: PressTarget, now: number = performance.now()): void {
  if (active) active.target.reset();
  active = {
    target,
    startedAt: now,
    releasedAt: null,
    releasedScale: 1,
    downDuration: getReducedMotionDuration(APP_PRESS_DOWN_MS, 48),
    reboundDuration: getReducedMotionDuration(APP_PRESS_REBOUND_MS, 140),
  };
  target.applyScale(1);
}

export function endPress(now: number = performance.now()): { downDurationMs: number } | null {
  if (!active || active.releasedAt !== null) return null;
  const downDurationMs = now - active.startedAt;
  active.releasedAt = now;
  active.releasedScale = computeDownScale(downDurationMs, active.downDuration);
  return { downDurationMs };
}

export function cancelPress(): void {
  if (!active) return;
  active.target.reset();
  active = null;
}

export function isPressActive(): boolean {
  return active !== null && active.releasedAt === null;
}

export function stepPressFeedback(now: number): void {
  if (!active) return;
  if (active.releasedAt === null) {
    const elapsed = now - active.startedAt;
    active.target.applyScale(computeDownScale(elapsed, active.downDuration));
    return;
  }
  // Rebound from releasedScale → 1 with overshoot; clamp to 1.08 to match
  // the prior tap-launch feel.
  const sinceRelease = now - active.releasedAt;
  const t = clamp(sinceRelease / Math.max(1, active.reboundDuration), 0, 1);
  const eased = isReducedMotion() ? easeOutCubic(t) : easeOutBack(t);
  const scale = clamp(
    lerp(active.releasedScale, 1, eased),
    Math.min(active.releasedScale, 1),
    1.08,
  );
  active.target.applyScale(scale);
  if (t >= 1) {
    active.target.reset();
    active = null;
  }
}
